import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from './ui/select';
import { toast } from 'sonner';
import { X, Loader2, Info, User, AlertTriangle, Users } from 'lucide-react';
import { SUPPORT_AREAS, SKILL_FOCUS_BY_AREA } from '../constants/supportAreas';

const SCORE_UNITS = ['%', 'wcpm', 'points', 'rating', 'frequency', 'problems correct', 'words', 'other'];

export default function AddInterventionModal({ studentId, student, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [staffProviders, setStaffProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // Check if student already has baseline/goal data
  const hasExistingBaseline = student?.baseline_score !== null && student?.baseline_score !== undefined;
  const hasExistingGoal = student?.goal_score !== null && student?.goal_score !== undefined;
  
  const [formData, setFormData] = useState({
    student_id: studentId,
    intervention_category: '',
    intervention_subcategory: '',
    custom_intervention_name: '',
    intervention_name: '', // Will be computed from category + subcategory
    target_skill: '',
    provider_name: '',
    assigned_provider_type: '',  // 'user' or 'staff_contact'
    assigned_specialist_user_id: '',  // For user-based providers
    assigned_provider_contact_id: '',  // For staff-contact-based providers
    frequency_per_week: 3,
    session_minutes: 30,
    group_size: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    active_status: 'Active',
    fidelity_notes: '',
    intervention_notes: '',
    // Intervention-specific baseline/goal
    baseline_score: '',
    goal_score: '',
    score_unit: '%' // Default to % as most common
  });

  // Track selected provider for display
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  // Separate state for student-level baseline data (for backwards compatibility)
  const [baselineData, setBaselineData] = useState({
    baseline_score: student?.baseline_score ?? '',
    baseline_measure_name: student?.baseline_measure_name ?? '',
    goal_score: student?.goal_score ?? ''
  });
  
  const [updateStudentBaseline, setUpdateStudentBaseline] = useState(!hasExistingBaseline);

  // Load eligible providers (App Users + Staff Contacts with can_provide_interventions)
  useEffect(() => {
    const loadProviders = async () => {
      if (!student?.school_id) return;
      setLoadingProviders(true);
      try {
        const res = await apiService.getEligibleInterventionProviders(student.school_id);
        setAppUsers(res.data.app_users || []);
        setStaffProviders(res.data.staff_providers || []);
      } catch (error) {
        console.error('Failed to load providers:', error);
      } finally {
        setLoadingProviders(false);
      }
    };
    loadProviders();
  }, [student?.school_id]);

  const handleProviderSelect = (providerKey) => {
    // providerKey format: "user_<id>" or "staff_<id>"
    const [type, id] = providerKey.split('_');
    
    let provider = null;
    if (type === 'user') {
      provider = appUsers.find(u => u.id === id);
      setFormData(prev => ({
        ...prev,
        assigned_provider_type: 'user',
        assigned_specialist_user_id: id,
        assigned_provider_contact_id: '',
        provider_name: provider?.display_name || ''
      }));
    } else if (type === 'staff') {
      provider = staffProviders.find(s => s.id === id);
      setFormData(prev => ({
        ...prev,
        assigned_provider_type: 'staff_contact',
        assigned_specialist_user_id: '',
        assigned_provider_contact_id: id,
        provider_name: provider?.display_name || ''
      }));
    }
    setSelectedProvider(provider);
  };

  const hasEligibleProviders = appUsers.length > 0 || staffProviders.length > 0;

  // Get subcategories based on selected category
  const subcategories = formData.intervention_category 
    ? SKILL_FOCUS_BY_AREA[formData.intervention_category] || []
    : [];

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset subcategory when category changes
      if (field === 'intervention_category') {
        updated.intervention_subcategory = '';
        updated.custom_intervention_name = '';
      }
      
      return updated;
    });
  };
  
  const handleBaselineChange = (field, value) => {
    setBaselineData({ ...baselineData, [field]: value });
  };

  // Compute intervention_name from category + subcategory
  const getInterventionName = () => {
    if (formData.intervention_category === 'Other') {
      return formData.custom_intervention_name || 'Custom Intervention';
    }
    if (formData.intervention_subcategory) {
      return formData.intervention_subcategory;
    }
    return formData.intervention_category || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.intervention_category) {
      toast.error('Please select an intervention category');
      return;
    }
    
    if (formData.intervention_category === 'Other' && !formData.custom_intervention_name) {
      toast.error('Please enter a custom intervention name');
      return;
    }

    if (!formData.target_skill) {
      toast.error('Please enter a target skill/goal');
      return;
    }

    // Require assigned provider (either user or staff contact)
    if (!formData.assigned_provider_type || (!formData.assigned_specialist_user_id && !formData.assigned_provider_contact_id)) {
      toast.error('Please select an Assigned Provider');
      return;
    }

    const interventionName = getInterventionName();

    setLoading(true);
    try {
      // Create the intervention with its own baseline/goal/unit
      await apiService.createIntervention({
        ...formData,
        intervention_name: interventionName,
        frequency_per_week: parseInt(formData.frequency_per_week),
        session_minutes: parseInt(formData.session_minutes),
        group_size: parseInt(formData.group_size),
        end_date: formData.end_date || null,
        baseline_score: formData.baseline_score !== '' ? parseFloat(formData.baseline_score) : null,
        goal_score: formData.goal_score !== '' ? parseFloat(formData.goal_score) : null,
        score_unit: formData.score_unit
      });
      
      // Update student baseline/goal if checkbox is checked and values are provided
      if (updateStudentBaseline) {
        const studentUpdate = {};
        if (baselineData.baseline_score !== '' && baselineData.baseline_score !== null) {
          studentUpdate.baseline_score = parseFloat(baselineData.baseline_score);
        }
        if (baselineData.baseline_measure_name) {
          studentUpdate.baseline_measure_name = baselineData.baseline_measure_name;
        }
        if (baselineData.goal_score !== '' && baselineData.goal_score !== null) {
          studentUpdate.goal_score = parseFloat(baselineData.goal_score);
        }
        
        if (Object.keys(studentUpdate).length > 0) {
          await apiService.updateStudent(studentId, studentUpdate);
        }
      }
      
      toast.success('Intervention added successfully');
      onSave();
    } catch (error) {
      console.error('Failed to create intervention:', error);
      toast.error('Failed to add intervention');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="add-intervention-modal" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Add Intervention</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="form-grid">
              {/* Intervention Category - Required */}
              <div className="form-group full-width">
                <Label htmlFor="intervention_category">Support Area *</Label>
                <Select 
                  value={formData.intervention_category} 
                  onValueChange={(v) => handleChange('intervention_category', v)}
                >
                  <SelectTrigger data-testid="intervention-category-select">
                    <SelectValue placeholder="Select a support area" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_AREAS.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Intervention Sub-Category - Optional (only shown if category has subcategories) */}
              {formData.intervention_category && subcategories.length > 0 && (
                <div className="form-group full-width">
                  <Label htmlFor="intervention_subcategory">Skill Focus (optional)</Label>
                  <Select 
                    value={formData.intervention_subcategory || 'none'} 
                    onValueChange={(v) => handleChange('intervention_subcategory', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger data-testid="intervention-subcategory-select">
                      <SelectValue placeholder="Select a skill focus (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None (use support area only) —</SelectItem>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Custom Intervention Name - Only for "Other" category */}
              {formData.intervention_category === 'Other' && (
                <div className="form-group full-width">
                  <Label htmlFor="custom_intervention_name">Custom Intervention Name *</Label>
                  <Input
                    id="custom_intervention_name"
                    value={formData.custom_intervention_name}
                    onChange={(e) => handleChange('custom_intervention_name', e.target.value)}
                    placeholder="Enter custom intervention name"
                    required
                    data-testid="custom-intervention-name-input"
                  />
                </div>
              )}

              {/* Preview of computed intervention name */}
              {formData.intervention_category && (
                <div className="form-group full-width">
                  <div className="p-2 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg">
                    <span className="text-xs text-[#595959]">Intervention Name: </span>
                    <span className="text-sm font-medium text-[#1C1C1C]">{getInterventionName() || '—'}</span>
                  </div>
                </div>
              )}

              <div className="form-group full-width">
                <Label htmlFor="target_skill">Target Skill / Goal *</Label>
                <Input
                  id="target_skill"
                  value={formData.target_skill}
                  onChange={(e) => handleChange('target_skill', e.target.value)}
                  placeholder="e.g., Decode CVC words, Multiply 2-digit numbers, Stay on task for 10 min"
                  required
                  data-testid="target-skill-input"
                />
                <p className="text-xs text-[#595959] mt-1">Enter a specific skill area or full goal statement</p>
              </div>

              <div className="form-group full-width">
                <Label htmlFor="assigned_provider">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Assigned Provider *
                  </div>
                </Label>
                <Select 
                  value={
                    formData.assigned_provider_type === 'user' && formData.assigned_specialist_user_id 
                      ? `user_${formData.assigned_specialist_user_id}` 
                      : formData.assigned_provider_type === 'staff_contact' && formData.assigned_provider_contact_id
                        ? `staff_${formData.assigned_provider_contact_id}`
                        : ''
                  } 
                  onValueChange={handleProviderSelect}
                  disabled={!hasEligibleProviders}
                >
                  <SelectTrigger data-testid="provider-select">
                    <SelectValue placeholder={loadingProviders ? "Loading..." : "Select a provider"} />
                  </SelectTrigger>
                  <SelectContent>
                    {appUsers.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-2 text-xs text-[#595959]">
                          <Users className="h-3 w-3" />
                          App Users (Can Login)
                        </SelectLabel>
                        {appUsers.map((user) => (
                          <SelectItem key={`user_${user.id}`} value={`user_${user.id}`}>
                            <span className="flex items-center gap-2">
                              {user.display_name}
                              <span className="text-[#A3A3A3]">({user.email})</span>
                              <span className="text-xs bg-[#2C4C3B]/10 text-[#2C4C3B] px-1.5 py-0.5 rounded">
                                {user.role_or_staff_type}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {staffProviders.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-2 text-xs text-[#595959]">
                          <User className="h-3 w-3" />
                          Staff Providers
                        </SelectLabel>
                        {staffProviders.map((staff) => (
                          <SelectItem key={`staff_${staff.id}`} value={`staff_${staff.id}`}>
                            <span className="flex items-center gap-2">
                              {staff.display_name}
                              <span className="text-[#A3A3A3]">({staff.email})</span>
                              <span className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] px-1.5 py-0.5 rounded">
                                {staff.role_or_staff_type}
                              </span>
                              {staff.login_status === 'linked_user' ? (
                                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Linked</span>
                              ) : (
                                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">No Login</span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
                {/* Show selected provider confirmation */}
                {selectedProvider && (
                  <div className="p-2 bg-green-50 border border-green-200 rounded-lg mt-2">
                    <p className="text-xs text-green-700">
                      <strong>Selected provider:</strong> {selectedProvider.display_name} ({selectedProvider.email})
                    </p>
                  </div>
                )}
                {!hasEligibleProviders && !loadingProviders && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      No eligible providers found. Create an Interventionist user or mark a staff contact as able to provide interventions.
                    </p>
                  </div>
                )}
                <p className="text-xs text-[#595959] mt-1">Required: Select the Interventionist or staff member who will implement this intervention</p>
              </div>

              <div className="form-group">
                <Label htmlFor="frequency_per_week">Times per Week</Label>
                <Input
                  id="frequency_per_week"
                  type="number"
                  min="1"
                  max="7"
                  value={formData.frequency_per_week}
                  onChange={(e) => handleChange('frequency_per_week', e.target.value)}
                  data-testid="frequency-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="session_minutes">Session Length (min)</Label>
                <Input
                  id="session_minutes"
                  type="number"
                  min="5"
                  max="120"
                  value={formData.session_minutes}
                  onChange={(e) => handleChange('session_minutes', e.target.value)}
                  data-testid="session-minutes-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="group_size">Group Size</Label>
                <Input
                  id="group_size"
                  type="number"
                  min="1"
                  max="30"
                  value={formData.group_size}
                  onChange={(e) => handleChange('group_size', e.target.value)}
                  data-testid="group-size-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="active_status">Status</Label>
                <Select value={formData.active_status} onValueChange={(v) => handleChange('active_status', v)}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Discontinued">Discontinued</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  data-testid="start-date-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  data-testid="end-date-input"
                />
              </div>
              
              {/* Baseline & Goal Section */}
              <div className="form-group full-width">
                <div className="border-t pt-4 mt-2">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium">Baseline & Goal</Label>
                      <p className="text-xs text-[#595959] mt-0.5">Set progress monitoring targets for this intervention</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <Label htmlFor="baseline_score" className="text-sm">Baseline Score</Label>
                      <Input
                        id="baseline_score"
                        type="number"
                        step="0.1"
                        value={formData.baseline_score}
                        onChange={(e) => handleChange('baseline_score', e.target.value)}
                        placeholder="e.g., 45"
                        data-testid="baseline-score-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="goal_score" className="text-sm">Goal Score</Label>
                      <Input
                        id="goal_score"
                        type="number"
                        step="0.1"
                        value={formData.goal_score}
                        onChange={(e) => handleChange('goal_score', e.target.value)}
                        placeholder="e.g., 75"
                        data-testid="goal-score-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="score_unit" className="text-sm">Score Unit</Label>
                      <Select value={formData.score_unit} onValueChange={(v) => handleChange('score_unit', v)}>
                        <SelectTrigger data-testid="score-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCORE_UNITS.map((unit) => (
                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="baseline_measure_name" className="text-sm">Measure Name</Label>
                      <Input
                        id="baseline_measure_name"
                        value={baselineData.baseline_measure_name}
                        onChange={(e) => handleBaselineChange('baseline_measure_name', e.target.value)}
                        placeholder="e.g., DIBELS ORF"
                        data-testid="baseline-measure-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <Label htmlFor="fidelity_notes">Fidelity Notes</Label>
                <Textarea
                  id="fidelity_notes"
                  value={formData.fidelity_notes}
                  onChange={(e) => handleChange('fidelity_notes', e.target.value)}
                  placeholder="Notes on implementation fidelity"
                  rows={2}
                  data-testid="fidelity-notes-input"
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="intervention_notes">Additional Notes</Label>
                <Textarea
                  id="intervention_notes"
                  value={formData.intervention_notes}
                  onChange={(e) => handleChange('intervention_notes', e.target.value)}
                  placeholder="Any other notes about this intervention"
                  rows={2}
                  data-testid="intervention-notes-input"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              disabled={loading}
              data-testid="save-intervention-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Intervention'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
